import { ActionIcon, Badge, Box, Group, Menu, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconTransfer, IconTrash, IconUserShare } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { Client } from 'src/admin/users/types';
import { useUpdateClient } from 'src/clients/hooks/useUpdateClient';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { NameCell } from 'src/common/components/table/NameCell';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { FilterRow, ViewMode } from './components/FilterRow';
import { useAdvisors } from './hooks/useAdvisors';
import { useClients } from './hooks/useClients';
import { useDeleteClient } from './hooks/useDeleteClient';
import TransferClientModal from 'src/home/TransferClientModal';
import { formatName } from 'src/utils/nameFormatting';

export function ClientsTable({
  title = 'Clients',
  titleIcon,
  onTitleClick,
  filters,
  showViewModes = true,
  primaryButton,
}: {
  title?: string;
  titleIcon?: React.ReactNode;
  onTitleClick?: () => void;
  filters?: Record<string, string>;
  showViewModes?: boolean;
  primaryButton?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary';
  };
}) {
  const [viewMode, setViewMode] = useState<ViewMode>('ViewAll');
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const { refetch } = useClients(filters);
  const { advisors } = useAdvisors();
  const [clientToTransfer, setClientToTransfer] = useState<Client | null>(null);
  const { currentUser } = useCurrentUser();
  const {
    deleteClient,
    error: deletionError,
    reset: resetDeleteClient,
    isLoading: isDeletingClient,
  } = useDeleteClient();
  const { isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const navigate = useNavigate();

  const { updateClient } = useUpdateClient();

  const advisorMap = useMemo(() => {
    if (!advisors) return {};

    return Object.fromEntries(advisors.map(advisor => [advisor.id, advisor]));
  }, [advisors]);

  const handleDeletePress = (client: Client) => {
    resetDeleteClient();
    setClientToDelete(client);
  };

  const handleSetToAdvised = async (client: Client) => {
    await updateClient(client.id, {
      advised: true,
      familyId: client.family?.id,
      advisorUserId: client.advisorUserId,
    });
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    await deleteClient(clientToDelete.id);
    setClientToDelete(null);
    refetch();
  };

  const columns = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      sortKey: 'lastName',
      render: (client: Client) => <NameCell entity={client} />,
    },
    {
      accessor: 'advised',
      title: 'Groups',
      render: (client: Client) => {
        const family = client.family;
        return (
          <Group>
            <Badge variant={client.advised ? 'brand' : 'pink'}>
              {client.advised ? 'Advised' : 'Non-advised'}
            </Badge>
            {family != null && (
              <Badge
                variant="green-clickable"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/clients/families/${family.id}`);
                }}
              >{`${family.name} Family`}</Badge>
            )}
          </Group>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      sortable: true,
      sortKey: 'createdAt',
      render: (client: Client) => {
        const date = new Date(client.createdAt);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        return (
          <Text size="sm" c="gray.7">
            {formattedDate}
          </Text>
        );
      },
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: Client) => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {currentUser && currentUser.userRole !== 'ADVISOR' && (
                <Menu.Item
                  leftSection={<IconUserShare size={14} />}
                  onClick={() => setClientToTransfer(client)}
                >
                  <Text>Transfer</Text>
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<IconTransfer size={14} />}
                onClick={() => handleSetToAdvised(client)}
                disabled={client.advised}
              >
                {client.advised ? (
                  <Tooltip label="Setting to non-advised is currently not supported via the platform. Please contact support.">
                    <Text>Set to non-advised</Text>
                  </Tooltip>
                ) : (
                  'Set to advised'
                )}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => handleDeletePress(client)}
              >
                Delete client
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  if (isSystemAdmin || isServiceAdmin) {
    columns.splice(2, 0, {
      accessor: 'advisor',
      title: 'Advisor',
      render: (client: Client) => {
        const advisorUserId = client.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" c="gray.7">
              No Advisor
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName)
          : 'No Advisor';
        return (
          <Text size="sm" c="gray.7">
            {advisorName}
          </Text>
        );
      },
    });
  }

  const filterPredicate = (client: Client, search: string) => {
    if (viewMode === 'ViewAdvised' && !client.advised) {
      return false;
    }

    if (viewMode === 'ViewNonAdvised' && client.advised) {
      return false;
    }

    if (search === '') {
      return true;
    }

    const searchableFields = [client.firstName, client.lastName, client.emailAddress];

    return searchableFields.some(field => field?.toLowerCase().includes(search.toLowerCase()));
  };

  return (
    <>
      <SimpleTable<Client>
        title={title}
        titleIcon={titleIcon}
        onTitleClick={onTitleClick}
        resource="clients"
        tableProps={{
          variant: 'clients-table',
        }}
        filters={filters}
        columns={columns}
        filterPredicate={filterPredicate}
        paginationDependencies={viewMode}
        onRowClick={client => {
          navigate(`/client/${client.id}/non-financial-data/personal-details`);
        }}
        HeaderRow={showViewModes && <FilterRow viewMode={viewMode} setViewMode={setViewMode} />}
        primaryButton={primaryButton}
      />
      {clientToTransfer && (
        <TransferClientModal
          currentAdvisorId={clientToTransfer.advisorUserId}
          clientId={clientToTransfer.id}
          opened={true}
          close={() => setClientToTransfer(null)}
          onTransferSuccess={async () => await refetch()}
        />
      )}
      <ConfirmDeleteModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete client"
        message={`Are you sure you want to delete ${formatName(clientToDelete?.firstName, clientToDelete?.lastName)}? This action cannot be undone.`}
        error={deletionError}
        isLoading={isDeletingClient}
      />
    </>
  );
}
